// Plain multi-file clip uploader for the publisher intake.
//
// Deliberately NOT the admin's BackgroundVideoBuilder: publishers hand us raw
// footage and nothing else. Trimming, ordering, and the 24-second stitch are
// CTVBuyer-side decisions the admin makes after the channel exists, so this
// surface has no timeline, no preview scrubbing, and no render.

import React from 'react';
import { Film, Loader2, Trash2, Upload } from 'lucide-react';
import {
  ACCEPTED_CLIP_TYPES,
  MAX_BACKGROUND_CLIPS,
  MAX_CLIP_BYTES,
  uploadBackgroundClip,
} from '../admin/backgroundVideoApi';
import type { UploadEndpoints } from '../admin/uploadEndpoints';
import { clipDisplayName } from './intakeClips';
import type { IntakeClipSpec } from '../../../types/publisherIntake';

interface Props {
  clips: IntakeClipSpec[];
  onChange: (next: IntakeClipSpec[]) => void;
  endpoints: UploadEndpoints;
}

const IntakeClipUploader: React.FC<Props> = ({ clips, onChange, endpoints }) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const remaining = MAX_BACKGROUND_CLIPS - clips.length;

  const uploadFiles = async (files: File[]) => {
    setError(null);
    if (files.length === 0) return;
    if (remaining <= 0) {
      setError(`You've already added ${MAX_BACKGROUND_CLIPS} clips.`);
      return;
    }

    const accepted: File[] = [];
    for (const file of files.slice(0, remaining)) {
      if (!ACCEPTED_CLIP_TYPES.includes(file.type)) {
        setError(`${file.name} isn't a supported video (use MP4, MOV, or WebM).`);
        continue;
      }
      if (file.size > MAX_CLIP_BYTES) {
        setError(`${file.name} is over ${Math.floor(MAX_CLIP_BYTES / 1024 / 1024)} MB.`);
        continue;
      }
      accepted.push(file);
    }
    if (files.length > remaining) {
      setError(`Only the first ${remaining} clip${remaining === 1 ? '' : 's'} were added — the limit is ${MAX_BACKGROUND_CLIPS}.`);
    }
    if (accepted.length === 0) return;

    setUploading(true);
    const added: IntakeClipSpec[] = [];
    try {
      // Sequential: these are large files and the API mints one signed URL at
      // a time against a per-intake budget.
      for (const file of accepted) {
        const storagePath = await uploadBackgroundClip(file, endpoints);
        added.push({ storagePath });
      }
      onChange([...clips, ...added]);
    } catch (e) {
      // Keep whatever did land so a failure partway through isn't total.
      if (added.length > 0) onChange([...clips, ...added]);
      setError(e instanceof Error ? e.message : 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const removeClip = (storagePath: string) =>
    onChange(clips.filter((c) => c.storagePath !== storagePath));

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          void uploadFiles(Array.from(e.dataTransfer.files));
        }}
        onClick={() => !uploading && remaining > 0 && inputRef.current?.click()}
        className={
          'rounded-xl border-2 border-dashed p-6 text-center transition-colors ' +
          (remaining <= 0
            ? 'border-slate-200 bg-slate-50 cursor-not-allowed'
            : 'cursor-pointer ' +
              (isDragging ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:border-slate-400'))
        }
        aria-disabled={uploading || remaining <= 0}
      >
        <div className="flex items-center justify-center gap-2 text-[13px] text-slate-600">
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Uploading…
            </>
          ) : remaining <= 0 ? (
            <>All {MAX_BACKGROUND_CLIPS} clips added</>
          ) : (
            <>
              <Upload className="w-4 h-4 text-slate-400" />
              Drop video clips, or{' '}
              <span className="font-semibold text-slate-900 underline underline-offset-2 decoration-1">
                browse
              </span>
            </>
          )}
        </div>
        <p className="mt-1 text-[11px] text-slate-400 uppercase tracking-[0.08em] font-medium">
          MP4 · MOV · WebM — up to {Math.floor(MAX_CLIP_BYTES / 1024 / 1024)} MB each, {MAX_BACKGROUND_CLIPS} clips max
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPTED_CLIP_TYPES.join(',')}
          onChange={(e) => {
            void uploadFiles(Array.from(e.target.files ?? []));
            e.target.value = '';
          }}
          className="hidden"
        />
      </div>

      {clips.length > 0 && (
        <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200">
          {clips.map((clip) => (
            <li key={clip.storagePath} className="flex items-center gap-3 px-4 py-2.5">
              <Film className="w-4 h-4 text-slate-400 shrink-0" />
              <span className="flex-1 min-w-0 truncate text-[13px] text-slate-700">
                {clipDisplayName(clip.storagePath)}
              </span>
              <button
                type="button"
                onClick={() => removeClip(clip.storagePath)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                aria-label={`Remove ${clipDisplayName(clip.storagePath)}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && <p className="text-[12px] text-rose-600">{error}</p>}
    </div>
  );
};

export default IntakeClipUploader;
